import { generatePKCEParams, retrievePKCEParams, storePKCEParams, type PKCEParams } from './pkce';
import type { WidgetAuthState } from './service';

/**
 * Frontend OAuth authentication manager for the widget
 * Handles complete OAuth flows and backend token validation
 */
export class FrontendAuthManager {
  private apiUrl: string;
  private portalUrl: string;
  private authState: WidgetAuthState;
  private listeners: Array<(state: WidgetAuthState) => void> = [];
  private allowedProviders?: string[];

  constructor(apiUrl: string, portalUrl?: string, allowedProviders?: string[]) {
    this.apiUrl = apiUrl;
    this.portalUrl = portalUrl || 'http://localhost:3000'; // Default to local portal
    
    // Validate and store allowed providers
    if (allowedProviders) {
      const validProviders = ['google', 'github', 'microsoft-entra-id', 'apple', 'linkedin'];
      const invalidProviders = allowedProviders.filter(p => !validProviders.includes(p));
      
      if (invalidProviders.length > 0) {
        console.warn(
          `[Komentas Widget] Invalid OAuth provider(s): ${invalidProviders.join(', ')}. ` +
          `Valid providers are: ${validProviders.join(', ')}`
        );
      }
      
      // Only store valid providers
      const filteredProviders = allowedProviders.filter(p => validProviders.includes(p));
      this.allowedProviders = filteredProviders.length > 0 ? filteredProviders : undefined;
      
      if (filteredProviders.length === 0 && allowedProviders.length > 0) {
        console.error(
          '[Komentas Widget] No valid OAuth providers configured. All providers will be shown by default.'
        );
      }
    }
    
    this.authState = {
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false,
      error: null,
    };
    
    this.loadFromStorage();
  }

  /**
   * Initialize OAuth login with specified provider
   */
  async login(provider: string): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      // Generate PKCE parameters on client side
      const pkceParams = await generatePKCEParams();
      
      // Call backend to get OAuth URL with backend-managed state
      // Backend will store the state and return the full OAuth URL
      const authUrlResponse = await fetch(
        `${this.apiUrl}/api/auth/oauth/${provider}/authorize?codeChallenge=${encodeURIComponent(pkceParams.codeChallenge)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!authUrlResponse.ok) {
        const errorData = await authUrlResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to get authorization URL');
      }

      const authUrlData = await authUrlResponse.json();
      
      // Backend returns: authorizationUrl, state, redirectUri
      const oauthUrl = authUrlData.authorizationUrl;
      const backendState = authUrlData.state;
      
      // Store PKCE params with backend's state (for client-side validation)
      const paramsToStore = {
        ...pkceParams,
        state: backendState  // Use backend's state
      };
      storePKCEParams(paramsToStore, provider);
      
      // Open OAuth popup with backend-provided URL
      const popup = window.open(
        oauthUrl,
        'oauth-login',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      // Wait for OAuth callback
      const result = await this.waitForOAuthCallback(popup, provider);
      
      if (result.success && result.code && result.state) {
        
        // Get stored params (with backend state) - this removes them from storage
        const storedParams = retrievePKCEParams(provider);
        if (!storedParams) {
          console.error('[WIDGET-AUTH] PKCE parameters not found in storage');
          throw new Error('PKCE parameters not found');
        }
        
        // Verify state parameter matches stored value (CSRF protection)
        if (storedParams.state !== result.state) {
          console.error('[WIDGET-AUTH] State mismatch - possible CSRF attack');
          throw new Error('Invalid state parameter - possible CSRF attack');
        }
        
        // Exchange authorization code for tokens
        await this.exchangeCodeForTokens(provider, result.code, storedParams);
      } else {
        throw new Error(result.error || 'OAuth authentication failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      this.setError(`Login failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    this.setLoading(true);
    
    try {
      // Clear local storage
      localStorage.removeItem('komentas_auth_token');
      localStorage.removeItem('komentas_auth_user');
      
      this.updateState({
        isAuthenticated: false,
        user: null,
        token: null,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Logout error:', error);
      this.setError('Logout failed');
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Get current authentication state
   */
  getState(): WidgetAuthState {
    return { ...this.authState };
  }

  /**
   * Subscribe to authentication state changes
   */
  subscribe(listener: (state: WidgetAuthState) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Get available OAuth providers
   */
  getAvailableProviders(): Array<{ id: string; name: string; icon: string }> {
    const allProviders = [
      { id: 'google', name: 'Google', icon: '🔍' },
      { id: 'github', name: 'GitHub', icon: '🐙' },
      { id: 'microsoft-entra-id', name: 'Microsoft', icon: '🪟' },
      { id: 'apple', name: 'Apple', icon: '🍎' },
      { id: 'linkedin', name: 'LinkedIn', icon: '💼' },
    ];

    // If allowedProviders is specified, filter the list
    if (this.allowedProviders && this.allowedProviders.length > 0) {
      return allProviders.filter(provider => 
        this.allowedProviders!.includes(provider.id)
      );
    }

    // Return all providers if no filter is specified
    return allProviders;
  }


  /**
   * Wait for OAuth callback from popup
   */
  /**
   * Wait for OAuth callback from popup with authorization code
   */
  private async waitForOAuthCallback(popup: Window, _provider: string): Promise<{
    success: boolean;
    code?: string;
    state?: string;
    error?: string;
  }> {
    return new Promise((resolve) => {
      // Use window.top for message listener if in iframe
      const targetWindow = window.top || window;
      
      // Listen for postMessage from OAuth callback popup
      const messageHandler = (event: MessageEvent) => {
        try {
          // Check if this is our OAuth callback message
          if (event.data && event.data.type === 'komentas-oauth-callback') {
            
            // Remove listener from target window
            targetWindow.removeEventListener('message', messageHandler);
            
            // Close popup if still open (with COOP error handling)
            try {
              if (popup && !popup.closed) {
                popup.close();
              }
            } catch (e) {
              // Ignore COOP errors - popup will close itself
            }
            
            if (event.data.success) {
              const code = event.data.code;
              const state = event.data.state;
              
              resolve({ 
                success: true, 
                code,
                state
              });
            } else {
              console.error('[WIDGET-AUTH] OAuth failed:', event.data.error);
              resolve({ 
                success: false, 
                error: event.data.errorDescription || event.data.error 
              });
            }
          }
        } catch (error) {
          console.error('[WIDGET-AUTH] ERROR in message handler:', error);
          resolve({ success: false, error: 'Message handler error: ' + (error as Error).message });
        }
      };
      
      // Add message listener to target window
      targetWindow.addEventListener('message', messageHandler);
      
      // Check if popup was closed manually (with COOP error handling)
      const checkPopupClosed = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopupClosed);
            targetWindow.removeEventListener('message', messageHandler);
            resolve({ success: false, error: 'OAuth popup was closed' });
          }
        } catch (e) {
          // Ignore COOP errors - expected when using Cross-Origin-Opener-Policy
        }
      }, 500);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(checkPopupClosed);
        targetWindow.removeEventListener('message', messageHandler);
        try {
          if (popup && !popup.closed) {
            popup.close();
          }
        } catch (e) {
          // Ignore COOP errors
        }
        resolve({ success: false, error: 'OAuth timeout' });
      }, 300000); // 5 minutes
    });
  }

  /**
   * Exchange authorization code for access tokens using PKCE
   */
  private async exchangeCodeForTokens(provider: string, code: string, pkceParams: PKCEParams): Promise<void> {
    try {
      // Prepare token exchange request
      const tokenRequest = {
        code,
        state: pkceParams.state,
        codeVerifier: pkceParams.codeVerifier,
        redirectUri: `${this.portalUrl}/oauth-callback`
      };

      // Call backend to exchange code for tokens
      const response = await fetch(`${this.apiUrl}/api/auth/oauth/${provider}/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tokenRequest),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Token exchange failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.isSuccess) {
        throw new Error(result.errorMessage || 'Token exchange failed');
      }

      // Store authentication state
      const authState = {
        isAuthenticated: true,
        user: {
          id: result.user.id,
          email: result.user.email,
          name: result.user.displayName,
          avatar: result.user.avatarUrl,
          provider: result.user.authProvider
        },
        token: result.token,
        isLoading: false,
        error: null,
      };

      this.updateState(authState);
      this.saveToStorage();
      
    } catch (error) {
      console.error('Token exchange error:', error);
      throw error;
    }
  }

  /**
   * Save authentication state to localStorage
   */
  private saveToStorage(): void {
    try {
      if (this.authState.isAuthenticated && this.authState.token && this.authState.user) {
        localStorage.setItem('komentas_auth_token', this.authState.token);
        localStorage.setItem('komentas_auth_user', JSON.stringify(this.authState.user));
      }
    } catch (error) {
      console.error('Failed to save auth state to storage:', error);
    }
  }

  /**
   * Load authentication state from localStorage
   */
  private loadFromStorage(): void {
    try {
      const token = localStorage.getItem('komentas_auth_token');
      const userStr = localStorage.getItem('komentas_auth_user');
      
      if (token && userStr) {
        // Validate token is not expired before loading
        if (this.isTokenExpired(token)) {
          console.log('Stored token is expired, clearing authentication state');
          localStorage.removeItem('komentas_auth_token');
          localStorage.removeItem('komentas_auth_user');
          localStorage.removeItem('komentas_refresh_token');
          return;
        }

        const user = JSON.parse(userStr);
        this.updateState({
          isAuthenticated: true,
          user,
          token,
          isLoading: false,
          error: null,
        });
      }
    } catch (error) {
      console.error('Failed to load auth state from storage:', error);
      // Clear invalid data
      localStorage.removeItem('komentas_auth_token');
      localStorage.removeItem('komentas_auth_user');
      localStorage.removeItem('komentas_refresh_token');
    }
  }

  /**
   * Check if JWT token is expired
   */
  private isTokenExpired(token: string): boolean {
    try {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        return true; // Invalid token format
      }

      // Decode the payload (base64url)
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      
      if (!payload.exp) {
        return false; // No expiration claim, consider it valid
      }

      // exp is in seconds, Date.now() is in milliseconds
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      
      // Add 60 second buffer - consider expired if less than 1 minute remaining
      return currentTime >= (expirationTime - 60000);
    } catch (error) {
      console.error('[Auth] Error checking token expiration:', error);
      return true; // If we can't parse it, consider it expired
    }
  }

  /**
   * Update authentication state and notify listeners
   */
  private updateState(newState: Partial<WidgetAuthState>): void {
    this.authState = { ...this.authState, ...newState };
    this.notifyListeners();
  }

  /**
   * Set loading state
   */
  private setLoading(isLoading: boolean): void {
    this.updateState({ isLoading });
  }

  /**
   * Set error state
   */
  private setError(error: string | null): void {
    this.updateState({ error });
  }

  /**
   * Notify all state change listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.getState());
      } catch (error) {
        console.error('Auth state listener error:', error);
      }
    });
  }
}

/**
 * Create and configure the frontend auth manager
 */
export function createFrontendAuth(apiUrl: string, portalUrl?: string, allowedProviders?: string[]): FrontendAuthManager {
  return new FrontendAuthManager(apiUrl, portalUrl, allowedProviders);
}